import { apiSlice } from "@/app/apiSlice";
import { API_ENDPOINTS } from "@/constants/apiEndpoints";
import { blobDownloadQuery } from "@/features/financialShared";
import type {
	CreateMonthlyServicePeriodBody,
	ExportMonthlyServicePeriodUnitsArgs,
	GetMonthlyServicePeriodsArgs,
	GetMonthlyServicePeriodUnitsArgs,
	GetMonthlyStatementPropertiesArgs,
	MonthlyServicePeriodsResponse,
	MonthlyServicePeriodUnitsResponse,
	MonthlyStatementPropertiesResponse,
	MonthlyUnitStatementPathArgs,
	UpdateMonthlyUnitStatementArgs,
	UploadMonthlyServicePeriodArgs,
} from "./monthlyStatements.api.types";
import {
	buildMonthlyServicePeriodListParams,
	buildMonthlyStatementListParams,
	parseMonthlyServicePeriodsResponse,
	parseMonthlyStatementPropertiesResponse,
} from "./monthlyStatements.mappers";
import type { MonthlyUnitDetail } from "./monthlyStatements.types";
import {
	buildMonthlyServicePeriodUnitsParams,
	parseMonthlyServicePeriodUnitsResponse,
	parseMonthlyUnitStatement,
} from "./monthlyStatements.unitMappers";

function unitsTag(propertyId: string, periodId: string) {
	return {
		type: "MonthlyStatementPeriodUnits" as const,
		id: `${propertyId}:${periodId}`,
	};
}

export const monthlyStatementsApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getMonthlyStatementProperties: builder.query<
			MonthlyStatementPropertiesResponse,
			GetMonthlyStatementPropertiesArgs | undefined
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.monthlyStatementManagement,
				params: buildMonthlyStatementListParams(args ?? {}),
			}),
			transformResponse: (raw: unknown) =>
				parseMonthlyStatementPropertiesResponse(raw),
			providesTags: [{ type: "MonthlyStatementProperties", id: "LIST" }],
		}),
		getMonthlyServicePeriods: builder.query<
			MonthlyServicePeriodsResponse,
			GetMonthlyServicePeriodsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.monthlyStatementServicePeriods(
					args.propertyId,
				),
				params: buildMonthlyServicePeriodListParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseMonthlyServicePeriodsResponse(raw, args.propertyId),
			providesTags: (_result, _error, args) => [
				{ type: "MonthlyStatementServicePeriods", id: args.propertyId },
			],
		}),
		createMonthlyServicePeriod: builder.mutation<
			unknown,
			{ propertyId: string; body: CreateMonthlyServicePeriodBody }
		>({
			query: ({ propertyId, body }) => ({
				url: API_ENDPOINTS.properties.monthlyStatementServicePeriods(
					propertyId,
				),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							{ type: "MonthlyStatementServicePeriods", id: arg.propertyId },
							{ type: "MonthlyStatementProperties", id: "LIST" },
						],
		}),
		downloadMonthlySampleXls: builder.mutation<DownloadedFile, void>({
			query: () =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.monthlyStatementServicePeriodSampleXls,
					"monthly-statement-sample.xls",
				),
		}),
		uploadMonthlyServicePeriod: builder.mutation<
			unknown,
			UploadMonthlyServicePeriodArgs
		>({
			query: ({ propertyId, periodId, body }) => ({
				url: API_ENDPOINTS.properties.uploadMonthlyStatementServicePeriod(
					propertyId,
					periodId,
				),
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							unitsTag(arg.propertyId, arg.periodId),
							{ type: "MonthlyStatementServicePeriods", id: arg.propertyId },
							{ type: "MonthlyStatementProperties", id: "LIST" },
							{ type: "MonthlyStatementUnitStatement", id: "LIST" },
						],
		}),
		getMonthlyServicePeriodUnits: builder.query<
			MonthlyServicePeriodUnitsResponse,
			GetMonthlyServicePeriodUnitsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.monthlyStatementServicePeriodUnits(
					args.propertyId,
					args.periodId,
				),
				params: buildMonthlyServicePeriodUnitsParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseMonthlyServicePeriodUnitsResponse(raw, {
					propertyId: args.propertyId,
					periodId: args.periodId,
				}),
			providesTags: (_result, _error, args) => [
				unitsTag(args.propertyId, args.periodId),
			],
		}),
		exportMonthlyServicePeriodUnits: builder.mutation<
			DownloadedFile,
			ExportMonthlyServicePeriodUnitsArgs
		>({
			query: ({ propertyId, periodId, scope }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.exportMonthlyStatementServicePeriodUnits(
						propertyId,
						periodId,
					),
					scope === "PENDING"
						? "monthly-units-pending.csv"
						: "monthly-units-all.csv",
					{ scope },
				),
		}),
		getMonthlyUnitStatement: builder.query<
			MonthlyUnitDetail,
			MonthlyUnitStatementPathArgs
		>({
			query: ({ propertyId, periodId, statementId }) => ({
				url: API_ENDPOINTS.properties.monthlyStatementUnitStatement(
					propertyId,
					periodId,
					statementId,
				),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseMonthlyUnitStatement(raw, args),
			providesTags: (_result, _error, args) => [
				{ type: "MonthlyStatementUnitStatement", id: args.statementId },
				{ type: "MonthlyStatementUnitStatement", id: "LIST" },
			],
		}),
		updateMonthlyUnitStatement: builder.mutation<
			unknown,
			UpdateMonthlyUnitStatementArgs
		>({
			query: ({ propertyId, periodId, statementId, body }) => ({
				url: API_ENDPOINTS.properties.monthlyStatementUnitStatement(
					propertyId,
					periodId,
					statementId,
				),
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							{ type: "MonthlyStatementUnitStatement", id: arg.statementId },
							unitsTag(arg.propertyId, arg.periodId),
						],
		}),
		exportMonthlyUnitCsv: builder.mutation<
			DownloadedFile,
			MonthlyUnitStatementPathArgs
		>({
			query: ({ propertyId, periodId, statementId }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.exportMonthlyStatementUnitCsv(
						propertyId,
						periodId,
						statementId,
					),
					"monthly-unit-statement.csv",
				),
		}),
		downloadMonthlyUnitStatementPdf: builder.mutation<
			DownloadedFile,
			MonthlyUnitStatementPathArgs
		>({
			query: ({ propertyId, periodId, statementId }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.exportMonthlyStatementUnitPdf(
						propertyId,
						periodId,
						statementId,
					),
					"monthly-unit-statement.pdf",
				),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetMonthlyStatementPropertiesQuery,
	useGetMonthlyServicePeriodsQuery,
	useCreateMonthlyServicePeriodMutation,
	useDownloadMonthlySampleXlsMutation,
	useUploadMonthlyServicePeriodMutation,
	useGetMonthlyServicePeriodUnitsQuery,
	useExportMonthlyServicePeriodUnitsMutation,
	useGetMonthlyUnitStatementQuery,
	useUpdateMonthlyUnitStatementMutation,
	useExportMonthlyUnitCsvMutation,
	useDownloadMonthlyUnitStatementPdfMutation,
} = monthlyStatementsApi;
